import { Injectable, inject } from '@angular/core';
import * as fromCatalogPlan from '@root-store/catalog-plan-store/index';
import { select, Store } from '@ngrx/store';
import { Subject, takeUntil, fromEvent, take, switchMap } from 'rxjs';
import Konva from 'konva';
import { CatalogPlanSeatResponseDto, SeatStatusEnumResponseDto } from '@core/services/apiclient';
import { SeatSelection } from '../models/catalog-plan.model';
import { CatalogPlanBlockViewUtilsService } from './catalog-plan-blockView-utils.service';

@Injectable({
  providedIn: 'root',
})
export class CatalogPlanBlockViewCommunicationService {
  private readonly store = inject<Store<fromCatalogPlan.State>>(Store);
  private readonly utils = inject(CatalogPlanBlockViewUtilsService);

  private destroy$ = new Subject<void>();
  private requestCacheRefresh$ = new Subject<string[]>();
  private selection: SeatSelection[] = [];
  seatLayer: Konva.Layer;
  contingentLayer: Konva.Layer;

  public init(contingentLayer: Konva.Layer, seatLayer: Konva.Layer) {
    this.seatLayer = seatLayer;
    this.contingentLayer = contingentLayer;
    setTimeout(() => {
      this.getCurrentUserRemoteSeatSelection();
    }, 1000);
  }

  public clearSelection() {
    this.selection = [];
  }

  /**
   * when the current user select a seat, dispatches an action to the store, calls the api to flag the seats
   * when the api responds with 200, the store is updated and this service receives the updated current selection
   */
  public getCurrentUserRemoteSeatSelection() {
    const selection$ = this.store.pipe(select(fromCatalogPlan.selectors.selectSeatSelection), take(1));
    fromEvent(document, 'click')
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => selection$),
      )
      .subscribe({
        next: (value: CatalogPlanSeatResponseDto[]) => {
          this.synchronizeWithRemoteSeatSelection(value);
        },
      });
  }

  /**
   * calls the selection sequence: store -> effect -> api to flag the seats
   * and select the same seats locally without waiting for the api response
   */
  public updateSeatSelection(selection: SeatSelection[]) {
    if (this.isSelectionBlocked(selection)) {
      return;
    }
    this.applySelectionToPlan(selection);
    this.store.dispatch(
      fromCatalogPlan.actions.updateSeatSelection({
        selection: selection.filter((value) => !!value).map(({ data }) => data),
      }),
    );
    this.contingentLayer.batchDraw();
  }

  public refreshSeatSelectionNodes(refreshedSeats: CatalogPlanSeatResponseDto[]) {
    const selectedSeats = refreshedSeats.filter((seat) =>
      this.selection.some((selectedSeat) => selectedSeat.seatId == seat.seatId),
    );
    if (!selectedSeats) {
      return;
    }
    this.removeUnselectedSeats(selectedSeats);
    const newSelectedSeats = this.convertToSeatSelection(selectedSeats);
    this.addNewSelectedSeats(newSelectedSeats);
    this.utils.highlightSelectedNodes(this.selection, this.seatLayer);
    this.contingentLayer.batchDraw();
    this.requestCacheRefreshForSelection(newSelectedSeats);
  }

  public resetSeatSelection() {
    this.applySelectionToPlan(this.selection);
    this.contingentLayer.batchDraw();
  }

  private isUnnumberedSeatSelectioned(seats: any) {
    return seats?.every((seat: { type: string }) => seat.type === 'unnumbered');
  }

  /**
   * if the new selection is included in the old selection array, then we must unselect the seats
   * else we add it to the old selection
   * finally, we request the cache to refresh on the affected parent groups
   */
  private applySelectionToPlan(newSelection: SeatSelection[]) {
    // the newSelection elements are being deflagged
    if (this.isUnselection(newSelection)) {
      this.removeUnselectedSeats(newSelection);
      this.utils.restoreSelectedNodes(newSelection, this.seatLayer);
    } else {
      this.addNewSelectedSeats(newSelection);
      this.utils.highlightSelectedNodes(this.selection, this.seatLayer);
    }
    this.contingentLayer.batchDraw();
    this.requestCacheRefreshForSelection(newSelection);
  }

  /**
   * this function is used to synchronize local & remote selections, if we have a delta between the two
   * we must synchonize the local selection to the remote one.
   * usecase: errors, if a seat is selected locally and the api responds with KO, we must reset this selection
   */
  private synchronizeWithRemoteSeatSelection(remoteSelection: CatalogPlanSeatResponseDto[]) {
    const _remoteSelection = this.convertToSeatSelection(remoteSelection);
    const seatsToRemove = this.selection.filter(
      ({ seatId }) => !_remoteSelection.some((seat) => seat?.seatId === seatId),
    );
    const seatsToAdd = _remoteSelection.filter(
      ({ seatId }) => !this.selection.some((seat) => seat?.seatId === seatId),
    );

    if (seatsToRemove?.length) {
      this.removeUnselectedSeats(seatsToRemove);
      this.utils.restoreSelectedNodes(seatsToRemove, this.seatLayer);
    }

    if (seatsToAdd?.length) {
      this.addNewSelectedSeats(seatsToAdd);
      this.utils.highlightSelectedNodes(this.selection, this.seatLayer);
    }
    this.contingentLayer.batchDraw();

    if (seatsToRemove?.length || seatsToAdd?.length) {
      this.requestCacheRefreshForSelection([...seatsToAdd, ...seatsToRemove]);
    }
  }

  /**
   * checks if selection or unselection:
   * unselection if every targeted element is included in the selection array
   * selection if atleast one element isn't in the selection array
   */
  private isUnselection(newSelection: SeatSelection[]) {
    return (
      !!newSelection?.length &&
      newSelection?.length <= this.selection?.length &&
      newSelection.every((selection) => this.selection.some(({ seatId }) => selection?.seatId === seatId))
    );
  }

  private isSelectionBlocked(selection: SeatSelection[]) {
    return selection?.every((seat) => seat?.data?.status.some((s) => s === SeatStatusEnumResponseDto.Locked));
  }

  private addNewSelectedSeats(newSelection: SeatSelection[]) {
    this.selection = [...this.selection, ...newSelection]
      .filter(({ seatId }, index, self) => self.findIndex((seat) => seat?.seatId === seatId) === index)
      .map((sel) => {
        const selectedSeat = this.selection.find((elt) => elt?.seatId === sel?.seatId);

        if (selectedSeat?.node) {
          return {
            ...selectedSeat,
            seatId: sel?.seatId,
          };
        }
        const seatNode: Konva.Group = this.utils.getSeatById(sel?.seatId);
        return {
          data: sel?.data,
          seatId: sel?.seatId,
          node: seatNode,
        };
      });
  }

  private removeUnselectedSeats(newSelection: SeatSelection[] | CatalogPlanSeatResponseDto[]) {
    this.selection = this.selection.filter(
      ({ seatId }) => !newSelection.some((seat) => seat?.seatId === seatId),
    );
  }

  private convertToSeatSelection(seats: CatalogPlanSeatResponseDto[]): SeatSelection[] {
    return (seats || []).map((value) => ({
      data: value,
      node: this.utils.getSeatById(value?.seatId),
      seatId: value?.seatId,
    }));
  }

  private requestCacheRefreshForSelection(selection: SeatSelection[]) {
    const parentGroupIds = selection
      .map(({ node }) => node?.name())
      .filter((groupId, index, self) => self.indexOf(groupId) === index);
    this.requestCacheRefresh(parentGroupIds);
  }

  public requestCacheRefresh(parentGroupIds: string[]) {
    this.requestCacheRefresh$.next(parentGroupIds);
  }

  public getCacheRefreshSignal() {
    return this.requestCacheRefresh$.asObservable();
  }

  public destroy() {
    this.destroy$.next();
  }
}
